import { useEffect, useRef, useState } from "react";
import { PALETTE as P } from "../../tokens";

/**
 * FoundationsRank — "Tap to Rank" (reorder tiles, top = highest priority).
 *
 * Output is an array of option values in priority order (index 0 = highest).
 * Reordering works two ways, no external library:
 *   - Drag-and-drop (pointer devices)
 *   - Up/down controls on each tile (touch-friendly + accessible)
 *
 * The initial order (as given) is emitted on mount so the prompt is
 * immediately answerable; the user reorders to taste.
 *
 * Props:
 *   options      — [{ value, label }]
 *   onRankChange — (rankedArray) => void
 */
export default function FoundationsRank({ options = [], onRankChange }) {
  const [order, setOrder] = useState(() => options.map((o) => o.value));
  const [dragIndex, setDragIndex] = useState(null);
  const labels = useRef(Object.fromEntries(options.map((o) => [o.value, o.label])));

  // Emit the initial ordering once so a no-reorder submit is still valid.
  useEffect(() => {
    onRankChange(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (next) => {
    setOrder(next);
    onRankChange(next);
  };

  const move = (from, to) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    commit(next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {order.map((value, i) => (
        <div
          key={value}
          draggable
          onDragStart={() => setDragIndex(i)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragIndex !== null) move(dragIndex, i);
            setDragIndex(null);
          }}
          onDragEnd={() => setDragIndex(null)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 14px",
            borderRadius: 12,
            border: `1px solid ${P.border}`,
            background: dragIndex === i ? P.primarySoft : P.bg,
            cursor: "grab",
            transition: "background 0.15s ease",
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: P.primary,
              width: 18,
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            {i + 1}
          </span>
          <span style={{ flex: 1, fontSize: 14, color: P.text, lineHeight: 1.4 }}>
            {labels.current[value]}
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
            <button
              type="button"
              aria-label="Move up"
              onClick={() => move(i, i - 1)}
              disabled={i === 0}
              style={chevronStyle(i === 0)}
            >
              ↑
            </button>
            <button
              type="button"
              aria-label="Move down"
              onClick={() => move(i, i + 1)}
              disabled={i === order.length - 1}
              style={chevronStyle(i === order.length - 1)}
            >
              ↓
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function chevronStyle(disabled) {
  return {
    width: 30,
    height: 22,
    borderRadius: 6,
    border: `1px solid ${P.border}`,
    background: "transparent",
    color: disabled ? P.textFaint : P.textMid,
    fontSize: 12,
    lineHeight: 1,
    cursor: disabled ? "default" : "pointer",
    padding: 0,
  };
}
